"use client";

import React, { useEffect, useState } from "react";
import { CareTabContent, CareSubTabItem } from "@/common/lib/types";
import PortableText from "@/components/common/PortableText";
import Image from "@/components/ui/imageV2/ImageV2";

interface CareTabVariantProps {
  data: CareTabContent;
}

const CareTabVariant: React.FC<CareTabVariantProps> = ({ data }) => {
  const subTabs = data?.subTabs?.filter((tab) => tab.enable !== false) || [];
  const [activeTabKey, setActiveTabKey] = useState<string>(
    subTabs[0]?.slug || subTabs[0]?.key || ""
  );

  const [activeVideoModal, setActiveVideoModal] = useState<{ title: string; videoUrl: string } | null>(null);

  const [openStepIndices, setOpenStepIndices] = useState<number[]>([0]);

  useEffect(() => {
    if (subTabs.length > 0 && !subTabs.some((tab) => (tab.slug || tab.key) === activeTabKey)) {
      setActiveTabKey(subTabs[0]?.slug || subTabs[0]?.key || "");
    }
  }, [subTabs, activeTabKey]);

  useEffect(() => {
    if (!activeVideoModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActiveVideoModal(null);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeVideoModal]);

  const toggleStep = (idx: number) => {
    setOpenStepIndices((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  if (!subTabs.length) return null;

  const currentSubTab =
    subTabs.find((tab) => (tab.slug || tab.key) === activeTabKey) || subTabs[0];

  const stepsSection = currentSubTab?.stepsSection;
  const videoSection = currentSubTab?.videoTutorialsSection;

  const steps = stepsSection?.steps?.filter((s) => s.enable !== false) || [];
  const videos = videoSection?.videos?.filter((v) => v.enable !== false) || [];

  return (
    <div className="container w-full py-[20px] md:py-[30px] flex flex-col gap-[20px] md:gap-[30px]">
      {/* Sub-Tabs Toggle */}
      {subTabs.length > 1 && (
        <div className="flex justify-start mb-2">
          <div className="inline-flex p-[5px] px-6 md:px-0 mx-auto md:mx-0">
            {subTabs.map((tab: CareSubTabItem) => {
              const tabKey = tab.slug || tab.key || "";
              const isActive = activeTabKey === tabKey;
              return (
                <button
                  key={tabKey}
                  type="button"
                  onClick={() => setActiveTabKey(tabKey)}
                  className={`px-6 py-2 rounded-none text-sm font-medium transition-all ${
                    isActive
                      ? "bg-[#2C322B] text-white shadow-sm"
                      : "text-[#2C322B] hover:text-black bg-transparent"
                  }`}
                >
                  {tab.title}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Steps Section */}
      {stepsSection && stepsSection.enable !== false && steps.length > 0 && (
        <section className="w-full px-6 md:px-0">
          {stepsSection.title && (
            <h2 className="text-[22px] font-bold text-black mb-6">
              {stepsSection.title}
            </h2>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((step, idx) => {
              const isOpen = openStepIndices.includes(idx);
              const stepNum = String(step.stepNumber || idx + 1).padStart(2, "0");

              return (
                <div
                  key={step.key || idx}
                  className="flex flex-col overflow-hidden rounded-md md:rounded-none border border-gray-100 md:border-none"
                  style={{ backgroundColor: "#f5f5f5" }}
                >
                  {step.image ? (
                    <div className="relative w-full h-[200px] md:h-[220px] overflow-hidden flex-shrink-0">
                      <Image
                        src={step.image}
                        alt={step.title || `Step ${idx + 1}`}
                        fill
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="mb-4 flex items-center justify-center h-11 w-11 rounded-full bg-[#e0e0e0]">
                      <span className="font-bold text-base text-black">
                        {step.stepNumber || idx + 1}
                      </span>
                    </div>
                  )}

                  <div className="p-4 flex-1" style={{ backgroundColor: "#f5f5f5" }}>
                    <button
                      type="button"
                      onClick={() => toggleStep(idx)}
                      className="w-full flex items-center justify-between text-left rtl:text-right focus:outline-none cursor-pointer md:cursor-default"
                    >
                      <div>
                        <span className="block text-[14px] font-bold tracking-[1.2px] text-black mb-1">
                           {stepNum}
                        </span>
                        {step.title && (
                          <h3 className=" text-black text-[16px] font-medium leading-snug">
                            {step.title}
                          </h3>
                        )}
                      </div>

                      {/* Accordion Chevron Icon for Mobile (< 768px) */}
                      <span className="md:hidden text-black transition-transform duration-200 ml-2">
                        <svg
                          className={`w-5 h-5 transform transition-transform duration-200 ${
                            isOpen ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </span>
                    </button>

                    {/* Step Description: Collapsible on Mobile, Always Visible on Desktop */}
                    {step.description && (
                      <div
                        className={`mt-2 text-[12px] text-tertiary-6 leading-relaxed [&_p]:mb-0 [&_p]:text-[12px] [&_p]:text-[#555555] ${
                          isOpen ? "block" : "hidden md:block"
                        }`}
                      >
                        <PortableText value={step.description} />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Video Tutorials Section */}
      {videoSection && videoSection.enable !== false && videos.length > 0 && (
        <section className="w-full pt-4 px-6 md:px-0">
          {videoSection.title && (
            <h2 className="text-[26px] md:text-[26px] font-medium text-black mb-6">
              {videoSection.title}
            </h2>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {videos.map((video, idx) => {
              const thumbnailSrc =
                (typeof video.desktopThumbnail === "string" ? video.desktopThumbnail : (video.desktopThumbnail as any)?.asset?.url) ||
                (typeof video.mobileThumbnail === "string" ? video.mobileThumbnail : (video.mobileThumbnail as any)?.asset?.url) ||
                "";
              const videoSrc =
                (typeof video.desktopVideo === "string" ? video.desktopVideo : (video.desktopVideo as any)?.asset?.url) ||
                (typeof video.mobileVideo === "string" ? video.mobileVideo : (video.mobileVideo as any)?.asset?.url) ||
                "";

              const openModal = () => {
                if (videoSrc) {
                  setActiveVideoModal({
                    title: video.title || "Video Tutorial",
                    videoUrl: videoSrc,
                  });
                }
              };

              return (
                <div key={video.key || idx} className="group cursor-pointer" onClick={openModal}>
                  <div className="relative w-full aspect-video overflow-hidden bg-gray-900 mb-3">
                    {thumbnailSrc ? (
                      <Image
                        src={thumbnailSrc}
                        alt={video.title || "Video thumbnail"}
                        fill
                        className="object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : videoSrc ? (
                      <video
                        src={videoSrc}
                        preload="metadata"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-800" />
                    )}
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 flex items-center justify-center transition-transform">
                        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="24" cy="24" r="21" stroke="#000000" strokeWidth="3.5" fill="transparent" />
                          <path d="M20 15L32 24L20 33V15Z" fill="#000000" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  {video.title && (
                    <h3 className="text-base md:text-base font-semibold text-black">
                      {video.title}
                    </h3>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Video Lightbox Modal Popup */}
      {activeVideoModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-0 md:p-6"
          onClick={() => setActiveVideoModal(null)}
        >
          <div
            className="bg-white rounded-none w-full md:w-[90vw] max-w-[980px] h-[479px] md:h-[759px] max-h-fit p-3 md:p-8 shadow-2xl flex flex-col animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 md:pb-6">
              <h3 className="text-lg md:text-[28px] font-semibold text-black">{activeVideoModal.title}</h3>
              <button
                type="button"
                className="text-black hover:opacity-70 text-2xl md:text-3xl font-light p-1 leading-none transition-opacity"
                onClick={() => setActiveVideoModal(null)}
                aria-label="Close modal"
              >
                &times;
              </button>
            </div>
            <div className="relative w-full aspect-video bg-black overflow-hidden flex items-center justify-center pdp-video-modal-player">
              <video
                src={activeVideoModal.videoUrl}
                controls
                autoPlay
                playsInline
                controlsList="nodownload"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareTabVariant;
